import { queryOptions } from '@tanstack/react-query';
import type { HttpTypes } from '@marxbiotech/shared-types/medusa';
import type {
  AlternativeSiteData,
  LayoutCustomerData,
  LayoutMainData,
  CheckoutLayoutData,
} from '@marxbiotech/shared-types/bff';

import { api } from '@lib/api/client';
import { CACHE_TIMES } from '@lib/query/cache-times';
import type {
  ProductCategory,
  CategoryHandleConfig,
} from '@modules/layout/components/product-nav';
import type {
  AccountNavItem,
} from '@modules/layout/components/mobile-nav';

export type { LayoutCustomerData };

/**
 * Build account nav items based on paymentCodEnabled flag.
 */
const buildAccountNavItems = (paymentCodEnabled: boolean): AccountNavItem[] => [
  {
    label: '會員資料',
    href: '/account',
  },
  {
    label: '儲值金紀錄',
    href: '/account/marx-payment-account',
  },
  ...(paymentCodEnabled
    ? [
        {
          label: '退貨退款餘額',
          href: '/account/marx-cod-refund-balance',
        },
      ]
    : []),
  {
    label: '收件地址',
    href: '/account/addresses',
  },
  {
    label: '訂單紀錄',
    links: [{ label: '全部訂單', href: '/account/orders' }],
  },
];

/** Shared fields present in every MainLayoutData variant. */
interface MainLayoutDataBase {
  categories: ProductCategory[];
  categoryHandleConfig: CategoryHandleConfig;
  region: HttpTypes.StoreRegion | null;
  paymentCodEnabled: boolean;
  navItems: AccountNavItem[];
  alternativeSite: AlternativeSiteData | null;
}

/**
 * Client-side layout data.
 *
 * Discriminated union on `isLoggedIn`:
 * - `false` → guest; `customer` is `null`, `isActive` is `false`.
 * - `true`  → authenticated; `customer` is non-null.
 */
export type MainLayoutData =
  | (MainLayoutDataBase & { isLoggedIn: false; customer: null; isActive: false })
  | (MainLayoutDataBase & { isLoggedIn: true; customer: LayoutCustomerData; isActive: boolean });

// Fetch layout data from BFF API
const fetchMainLayoutData = async (countryCode: string): Promise<MainLayoutData> => {
  const response = await api.get<LayoutMainData>('/layout/main', {
    params: { country_code: countryCode },
  });
  const data = response.data;
  const paymentCodEnabled = data.navConfig?.paymentCodEnabled ?? false;

  const base: MainLayoutDataBase = {
    categories: (data.categories ?? []) as ProductCategory[],
    categoryHandleConfig: data.categoryHandleConfig,
    region: data.region ?? null,
    paymentCodEnabled,
    navItems: buildAccountNavItems(paymentCodEnabled),
    alternativeSite: data.alternativeSite ?? null,
  };

  if (!data.isLoggedIn) {
    return { ...base, isLoggedIn: false as const, customer: null, isActive: false as const };
  }

  return {
    ...base,
    isLoggedIn: true as const,
    customer: data.customer,
    isActive: data.isActive,
  };
};

export const layoutKeys = {
  all: ['layout'] as const,
  main: (countryCode: string) => [...layoutKeys.all, 'main', countryCode] as const,
};

export const mainLayoutQueryOptions = (countryCode: string) =>
  queryOptions({
    queryKey: layoutKeys.main(countryCode),
    queryFn: () => fetchMainLayoutData(countryCode),
    // MainLayout 使用 useSuspenseQuery 預先載入，子頁面用 useQuery 讀取快取。
    // 設為 Infinity 確保快取永不過期，避免子頁面 refetch 失敗時無法處理錯誤。
    staleTime: Infinity,
    gcTime: CACHE_TIMES.medium.gcTime,
  });

// Fetch checkout layout data from BFF API
const fetchCheckoutLayoutData = async (): Promise<CheckoutLayoutData> => {
  const response = await api.get<CheckoutLayoutData>('/layout/checkout');
  return response.data;
};

export const checkoutLayoutQueryOptions = () =>
  queryOptions({
    queryKey: [...layoutKeys.all, 'checkout'] as const,
    queryFn: fetchCheckoutLayoutData,
    staleTime: CACHE_TIMES.medium.staleTime,
    gcTime: CACHE_TIMES.medium.gcTime,
  });
